/**
 * Shared Tree Diff + Changelog + Detail view components.
 * Extracted from CommitDiffModal for reuse in Compare page and Merge Preview.
 */
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { FileText, FolderOpen, ChevronDown, ChevronRight, ArrowLeft, Eye } from "lucide-react";
import { authFetch } from "../../utils/authFetch";
import RenderedDiffView from "./RenderedDiffView";

/* ── Shared types ──────────────────────────────────────────────────── */

export interface TreeNode {
  id: string;
  slug: string;
  slugPath: string;
  title: string;
  nodeType: string;
  parentId: string | null;
  parentPath: string | null;
  order: string[];
  contentHash: string | null;
  visibility: string;
  lastEdit?: {
    userId: string | null;
    userEmail: string | null;
    userName: string | null;
    at: string;
  } | null;
}

export interface ChangeEntry {
  slugPath: string;
  actions: string[];
  changeTypes?: string[];
  originalSlugPath?: string;
  changedAssets?: { filename: string; action: string }[];
  beforeBody?: string;
  afterBody?: string;
  beforeTitle?: string;
  afterTitle?: string;
  nodeType?: string;
}

export interface MergedNode {
  slug: string;
  slugPath: string;
  title: string;
  sourceTitle: string | null;
  draftTitle: string | null;
  statuses: string[];
  nodeType: string;
  change?: ChangeEntry;
  children: MergedNode[];
  sourceOnly?: boolean;
  draftOnly?: boolean;
}

export type Tab = "tree" | "changelog";

export type FlatItem = {
  slug: string; slugPath: string; title: string; statuses: string[];
  change?: ChangeEntry; sourceOnly?: boolean; draftOnly?: boolean; childCount?: number;
};

export type GroupedRow =
  | { kind: "single"; item: FlatItem }
  | { kind: "group"; status: "added" | "deleted"; parentPath: string; parentTitle: string; items: FlatItem[] };

export interface DetailData {
  slugPath: string;
  title: string;
  change: ChangeEntry;
  beforeBody: string;
  afterBody: string;
  nodeType?: string;
  beforeTitle?: string;
  afterTitle?: string;
  changedAssets?: { filename: string; action: string }[];
  beforeSpec?: Record<string, any> | null;
  afterSpec?: Record<string, any> | null;
  /** Support / FAQ typed metadata blobs (summary, tags, modules,
   *  featured). Populated only for pages inside a
   *  section_type='support' or 'faq' subtree — RenderedDiffView
   *  routes these to SupportMetaDiffView. */
  beforeMeta?: Record<string, any> | null;
  afterMeta?: Record<string, any> | null;
  /** Section type of the containing section — used by the diff view
   *  to pick the right typed-metadata renderer (currently only
   *  support / faq). */
  sectionType?: string | null;
}

/* ── Build merged tree from before/after snapshots ── */

export function buildDiffMergedTree(
  beforeNodes: TreeNode[],
  afterNodes: TreeNode[],
  changes: ChangeEntry[],
  beforeRootOrder: string[],
  afterRootOrder: string[],
): MergedNode[] {
  const changeByPath = new Map<string, ChangeEntry>();
  for (const c of changes) {
    changeByPath.set(c.slugPath, c);
    // Also index by originalSlugPath so the before-tree node gets the move badge
    if (c.originalSlugPath) changeByPath.set(c.originalSlugPath, c);
  }

  const beforeByPath = new Map<string, TreeNode>();
  for (const n of beforeNodes) beforeByPath.set(n.slugPath, n);
  const afterByPath = new Map<string, TreeNode>();
  for (const n of afterNodes) afterByPath.set(n.slugPath, n);

  function buildChildIndex(nodes: TreeNode[], rootOrder: string[]) {
    const childMap = new Map<string, Map<string, TreeNode>>();
    for (const n of nodes) {
      const parentKey = n.parentPath && nodes.some(x => x.slugPath === n.parentPath)
        ? n.parentPath : "__root__";
      if (!childMap.has(parentKey)) childMap.set(parentKey, new Map());
      childMap.get(parentKey)!.set(n.slug, n);
    }
    const orderMap = new Map<string, string[]>();
    const rootKids = childMap.get("__root__");
    if (rootKids) {
      const order = rootOrder.length ? [...rootOrder] : [];
      const inOrder = new Set(order);
      for (const s of rootKids.keys()) if (!inOrder.has(s)) order.push(s);
      orderMap.set("__root__", order.filter(s => rootKids.has(s)));
    }
    for (const n of nodes) {
      const kids = childMap.get(n.slugPath);
      if (!kids || kids.size === 0) continue;
      const order = n.order?.length ? [...n.order] : [];
      const inOrder = new Set(order);
      for (const s of kids.keys()) if (!inOrder.has(s)) order.push(s);
      orderMap.set(n.slugPath, order.filter(s => kids.has(s)));
    }
    return { childMap, orderMap };
  }

  const bIdx = buildChildIndex(beforeNodes, beforeRootOrder);
  const aIdx = buildChildIndex(afterNodes, afterRootOrder);

  function mergeSlugs(a: string[], b: string[]): string[] {
    const aSet = new Set(a), bSet = new Set(b);
    const out: string[] = [];
    let ai = 0, bi = 0;
    while (ai < a.length && bi < b.length) {
      if (a[ai] === b[bi]) { out.push(a[ai]); ai++; bi++; }
      else if (!bSet.has(a[ai])) { out.push(a[ai]); ai++; }
      else if (!aSet.has(b[bi])) { out.push(b[bi]); bi++; }
      else { out.push(b[bi]); bi++; }
    }
    while (ai < a.length) out.push(a[ai++]);
    while (bi < b.length) out.push(b[bi++]);
    const seen = new Set<string>();
    return out.filter(s => { if (seen.has(s)) return false; seen.add(s); return true; });
  }

  function buildOneSide(parentSlugPath: string, slug: string, side: "before" | "after"): MergedNode {
    const idx = side === "before" ? bIdx : aIdx;
    const sp = parentSlugPath === "__root__" ? slug : parentSlugPath + "/" + slug;
    const node = (side === "before" ? beforeByPath : afterByPath).get(sp);
    const change = changeByPath.get(sp);
    const defaultStatuses: string[] = side === "before" ? ["deleted"] : ["added"];
    const kidSlugs = idx.orderMap.get(sp) || [];
    return {
      slug, title: node?.title || slug, slugPath: sp,
      sourceTitle: side === "before" ? (node?.title || slug) : null,
      draftTitle: side === "after" ? (node?.title || slug) : null,
      statuses: change ? change.actions : defaultStatuses,
      nodeType: node?.nodeType || "section",
      change,
      children: kidSlugs.map(s => buildOneSide(sp, s, side)),
      sourceOnly: side === "before" ? true : undefined,
      draftOnly: side === "after" ? true : undefined,
    };
  }

  function build(parentSlugPath: string): MergedNode[] {
    const bs = bIdx.orderMap.get(parentSlugPath) || [];
    const as_ = aIdx.orderMap.get(parentSlugPath) || [];
    const merged = mergeSlugs(bs, as_);
    const bSet = new Set(bs), aSet = new Set(as_);
    const result: MergedNode[] = [];
    for (const slug of merged) {
      const inB = bSet.has(slug);
      const inA = aSet.has(slug);
      const sp = parentSlugPath === "__root__" ? slug : parentSlugPath + "/" + slug;
      if (inB && !inA) {
        result.push(buildOneSide(parentSlugPath, slug, "before"));
      } else if (!inB && inA) {
        result.push(buildOneSide(parentSlugPath, slug, "after"));
      } else {
        const bn = beforeByPath.get(sp);
        const an = afterByPath.get(sp);
        const change = changeByPath.get(sp);
        const statuses: string[] = change ? change.actions : [];
        result.push({
          slug, title: an?.title || bn?.title || slug, slugPath: sp,
          sourceTitle: bn?.title || slug, draftTitle: an?.title || slug,
          statuses, nodeType: an?.nodeType || bn?.nodeType || "section",
          change,
          children: build(sp),
        });
      }
    }
    return result;
  }

  return build("__root__");
}

export function subtreeHasChanges(node: MergedNode): boolean {
  if (node.statuses.length > 0 || node.sourceOnly || node.draftOnly) return true;
  return node.children.some(subtreeHasChanges);
}

/* ── Changelog grouping ── */

export function computeGroupedRows(mergedTree: MergedNode[]): GroupedRow[] {
  const list: FlatItem[] = [];

  /** Check if an entire subtree has a single uniform status (e.g. all "added"). */
  function isEntireSubtree(n: MergedNode, st: string): boolean {
    if (!(n.statuses.length === 1 && n.statuses[0] === st)) return false;
    return n.children.every(c => isEntireSubtree(c, st));
  }

  function collectLeaves(n: MergedNode, out: FlatItem[]) {
    if (n.children.length === 0) {
      out.push({ slug: n.slug, slugPath: n.slugPath, title: n.title, statuses: n.statuses, change: n.change, sourceOnly: n.sourceOnly, draftOnly: n.draftOnly });
    }
    for (const c of n.children) collectLeaves(c, out);
  }

  function walk(nodes: MergedNode[]) {
    for (const n of nodes) {
      if (n.statuses.length === 0 && !n.sourceOnly && !n.draftOnly) { walk(n.children); continue; }
      const primaryStatus = n.statuses[0];
      const bulkStatus = primaryStatus === "added" || primaryStatus === "deleted";
      if (bulkStatus && n.statuses.length === 1 && n.children.length > 0 && isEntireSubtree(n, primaryStatus)) {
        const leaves: FlatItem[] = [];
        collectLeaves(n, leaves);
        list.push({ slug: n.slug, slugPath: n.slugPath, title: n.title, statuses: n.statuses, change: n.change, sourceOnly: n.sourceOnly, draftOnly: n.draftOnly, childCount: leaves.length });
        for (const l of leaves) list.push(l);
        continue;
      }
      if (n.children.length === 0 || n.change) {
        list.push({ slug: n.slug, slugPath: n.slugPath, title: n.title, statuses: n.statuses, change: n.change, sourceOnly: n.sourceOnly, draftOnly: n.draftOnly });
      }
      walk(n.children);
    }
  }
  walk(mergedTree);

  const result: GroupedRow[] = [];
  const parentPaths = new Set<string>();
  for (const item of list) if (item.childCount != null && item.childCount > 0) parentPaths.add(item.slugPath);
  for (const item of list) {
    if (parentPaths.has(item.slugPath)) {
      const children = list.filter(c => c !== item && c.slugPath.startsWith(item.slugPath + "/"));
      const groupStatus = (item.statuses[0] === "added" || item.statuses[0] === "deleted") ? item.statuses[0] as "added" | "deleted" : "added";
      result.push({ kind: "group", status: groupStatus, parentPath: item.slugPath, parentTitle: item.title, items: children });
    } else {
      let belongsToGroup = false;
      for (const pp of parentPaths) if (item.slugPath.startsWith(pp + "/")) { belongsToGroup = true; break; }
      if (!belongsToGroup) result.push({ kind: "single", item });
    }
  }
  return result;
}

/* ── Shared Tab button ── */

export function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`flex items-center px-3 py-2 text-[11px] font-medium border-b-2 transition-colors ${
        active ? "border-indigo-500 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}>{children}</button>
  );
}

/* ── Tree diff view ── */

export function TreeDiffView({ mergedTree, expandMap, onToggle, onSelect }: {
  mergedTree: MergedNode[];
  expandMap: Record<string, boolean>;
  onToggle: (sp: string) => void;
  onSelect: (c: ChangeEntry) => void;
}) {
  if (mergedTree.length === 0) {
    return <div className="py-8 text-center text-[12px] text-slate-400">No changes.</div>;
  }
  return (
    <div className="flex-1 overflow-y-auto scrollbar-mac">
      <div className="px-5 py-4">
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-2 border-b border-slate-200 sticky top-0 z-10">
            <div className="px-3 py-2 bg-red-50/70 flex items-center gap-1.5 border-r border-slate-200">
              <div className="size-2 rounded-full bg-red-400" />
              <span className="text-[11px] font-semibold text-red-800">Before</span>
            </div>
            <div className="px-3 py-2 bg-emerald-50/70 flex items-center gap-1.5">
              <div className="size-2 rounded-full bg-emerald-400" />
              <span className="text-[11px] font-semibold text-emerald-800">After</span>
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto scrollbar-mac">
            {mergedTree.map(n => (
              <DiffMergedRow key={n.slugPath} node={n} depth={0} expandMap={expandMap} onToggle={onToggle} onSelect={onSelect} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Two-column merged row ── */

export function DiffMergedRow({ node, depth, expandMap, onToggle, onSelect }: {
  node: MergedNode; depth: number;
  expandMap: Record<string, boolean>; onToggle: (sp: string) => void;
  onSelect: (c: ChangeEntry) => void;
}) {
  const hasChildren = node.children.length > 0;
  const expanded = expandMap[node.slugPath] ?? false;
  const { statuses } = node;
  const hasChangesBelow = useMemo(() => subtreeHasChanges(node), [node]);
  const collapsedWithChanges = hasChildren && !expanded && hasChangesBelow;
  const isClickable = !!node.change;

  const BADGE_CFG: Record<string, { label: string; bg: string; text: string }> = {
    added:     { label: "ADD", bg: "bg-emerald-100", text: "text-emerald-600" },
    deleted:   { label: "DEL", bg: "bg-red-100",     text: "text-red-500" },
    modified:  { label: "MOD", bg: "bg-blue-100",    text: "text-blue-600" },
    moved:     { label: "MOVE", bg: "bg-violet-100", text: "text-violet-600" },
    renamed:   { label: "REN", bg: "bg-fuchsia-100", text: "text-fuchsia-600" },
    reordered: { label: "ORD", bg: "bg-amber-100",   text: "text-amber-600" },
  };

  const renderCell = (side: "before" | "after") => {
    const isGhost =
      (node.sourceOnly && side === "after") ||
      (node.draftOnly && side === "before") ||
      (statuses.includes("added") && side === "before") ||
      (statuses.includes("deleted") && side === "after");

    if (isGhost) {
      const ghostBg =
        statuses.includes("added") ? "bg-emerald-50/30" :
        statuses.includes("deleted") ? "bg-red-50/30" :
        "bg-slate-50/30";
      return <div style={{ paddingLeft: depth * 14 }} className={`flex items-center py-[3px] px-1 text-[10px] ${ghostBg}`} />;
    }

    let cellClass = "text-slate-600";
    if (node.sourceOnly && statuses.includes("deleted")) cellClass = "text-red-500";
    else if (node.draftOnly && statuses.includes("added")) cellClass = "bg-emerald-50/60 text-emerald-800";
    else if (statuses.includes("moved")) cellClass = "bg-violet-50/60 text-violet-800";
    else if (statuses.includes("modified")) cellClass = "bg-blue-50/60 text-blue-800";
    else if (statuses.includes("added")) cellClass = side === "after" ? "bg-emerald-50/60 text-emerald-800" : "text-slate-600";
    else if (statuses.includes("deleted")) cellClass = side === "before" ? "text-red-500" : "text-slate-600";
    else if (statuses.includes("reordered")) cellClass = "bg-amber-50/60 text-amber-800";

    const collapsedAccent = collapsedWithChanges ? "border-l-2 border-l-amber-400" : "";

    // Render multi-tag badges on the "after" side (or "before" for deleted)
    const showBadges = (statuses.includes("deleted") && side === "before") ||
                       (!statuses.includes("deleted") && side === "after");
    const badges = showBadges ? (
      <span className="flex items-center gap-0.5 ml-auto shrink-0">
        {statuses.map(s => {
          const cfg = BADGE_CFG[s]; if (!cfg) return null;
          return <span key={s} className={`text-[8px] px-1 py-px rounded font-semibold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
        })}
      </span>
    ) : null;

    const title = side === "before" ? (node.sourceTitle || node.title) : (node.draftTitle || node.title);

    return (
      <div style={{ paddingLeft: depth * 14 }} className={`flex items-center gap-1 py-[3px] px-1 text-[10px] ${cellClass} ${collapsedAccent}`}>
        {hasChildren ? (
          <button onClick={(e) => { e.stopPropagation(); onToggle(node.slugPath); }} className="shrink-0 p-0">
            {expanded ? <ChevronDown className="size-3 text-slate-400" /> : <ChevronRight className="size-3 text-slate-400" />}
          </button>
        ) : <span className="w-3 shrink-0" />}
        {hasChildren
          ? <FolderOpen className="size-3 text-amber-500 shrink-0" />
          : <FileText className="size-3 text-slate-400 shrink-0" />}
        <span className="truncate">{title}</span>
        {collapsedWithChanges && !badges && (
          <span className="size-1.5 rounded-full bg-amber-400 ml-auto shrink-0" title="Changes inside" />
        )}
        {badges}
      </div>
    );
  };

  return (
    <div>
      <div
        className={`grid grid-cols-2 ${isClickable ? "cursor-pointer hover:bg-slate-50/80" : ""}`}
        onClick={isClickable && node.change ? () => onSelect(node.change!) : undefined}
      >
        <div className="border-r border-slate-100">{renderCell("before")}</div>
        <div>{renderCell("after")}</div>
      </div>
      {expanded && hasChildren && node.children.map(c => (
        <DiffMergedRow key={c.slugPath} node={c} depth={depth + 1} expandMap={expandMap} onToggle={onToggle} onSelect={onSelect} />
      ))}
    </div>
  );
}

/* ── Detail view (side-by-side content) ── */

export function DetailView({ detail, detailLoading, onBack }: { detail: DetailData; detailLoading: boolean; onBack: () => void }) {
  return (
    <RenderedDiffView
      beforeBody={detail.change.actions.includes("added") ? "" : (detail.beforeBody || "")}
      afterBody={detail.change.actions.includes("deleted") ? "" : (detail.afterBody || "")}
      title={detail.title}
      slugPath={detail.slugPath}
      changeActions={detail.change.actions}
      changeTypes={detail.change.changeTypes}
      loading={detailLoading}
      onBack={onBack}
      nodeType={detail.nodeType}
      beforeTitle={detail.beforeTitle}
      afterTitle={detail.afterTitle}
      changedAssets={detail.changedAssets}
    />
  );
}

/* ── Changelog view ── */

export function ChangelogView({ rows, onSelect }: { rows: GroupedRow[]; onSelect: (c: ChangeEntry) => void }) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (rows.length === 0) {
    return <div className="flex-1 py-8 text-center text-[12px] text-slate-400">No changes to show.</div>;
  }

  const badgeCfg: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    added:     { label: "ADD",  bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-400" },
    deleted:   { label: "DEL",  bg: "bg-red-50",     text: "text-red-500",     dot: "bg-red-400" },
    modified:  { label: "MOD",  bg: "bg-blue-50",    text: "text-blue-600",    dot: "bg-blue-400" },
    moved:     { label: "MOVE", bg: "bg-violet-50",  text: "text-violet-600",  dot: "bg-violet-400" },
    renamed:   { label: "REN",  bg: "bg-fuchsia-50", text: "text-fuchsia-600", dot: "bg-fuchsia-400" },
    reordered: { label: "ORD",  bg: "bg-amber-50",   text: "text-amber-600",   dot: "bg-amber-400" },
  };

  return (
    <div className="flex-1 overflow-y-auto scrollbar-mac px-5 py-4">
      <div className="space-y-1">
        {rows.map((row, i) => {
          if (row.kind === "single") {
            const item = row.item;
            const primaryStatus = item.statuses[0] || "modified";
            const cfg = badgeCfg[primaryStatus] || badgeCfg.modified;
            return (
              <div key={item.slugPath + "-" + primaryStatus}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100 transition-colors ${
                  item.change ? "hover:bg-slate-50/80 cursor-pointer" : ""
                }`}
                onClick={item.change ? () => onSelect(item.change!) : undefined}
              >
                <div className={`size-2 rounded-full shrink-0 ${cfg.dot}`} />
                <div className="flex items-center gap-0.5 shrink-0">
                  {item.statuses.map(s => {
                    const b = badgeCfg[s]; if (!b) return null;
                    return <span key={s} className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${b.bg} ${b.text}`}>{b.label}</span>;
                  })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-medium text-slate-700 truncate">{item.title}</div>
                  <div className="text-[9px] text-slate-400 font-mono truncate">{item.slugPath}</div>
                </div>
              </div>
            );
          }
          const cfg = badgeCfg[row.status];
          const groupKey = `group-${row.status}-${row.parentPath}`;
          const expanded = expandedGroups[groupKey] ?? false;
          const label = row.status === "added"
            ? `Added ${row.parentTitle} (${row.items.length} items)`
            : `Deleted ${row.parentTitle} (${row.items.length} items)`;
          return (
            <div key={groupKey + "-" + i}>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100 hover:bg-slate-50/80 cursor-pointer transition-colors"
                onClick={() => toggleGroup(groupKey)}>
                <div className={`size-2 rounded-full shrink-0 ${cfg.dot}`} />
                <span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-medium text-slate-700 truncate">{label}</div>
                  <div className="text-[9px] text-slate-400 font-mono truncate">{row.parentPath || "/"}</div>
                </div>
                {expanded ? <ChevronDown className="size-3 text-slate-400 shrink-0" /> : <ChevronRight className="size-3 text-slate-400 shrink-0" />}
              </div>
              {expanded && (
                <div className="ml-4 mt-1 mb-1 border-l-2 border-slate-100 pl-3 space-y-px">
                  {row.items.map(item => (
                    <div key={item.slugPath}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors ${
                        item.change ? "hover:bg-slate-50 cursor-pointer" : ""
                      }`}
                      onClick={item.change ? () => onSelect(item.change!) : undefined}
                    >
                      <FileText className="size-3 text-slate-300 shrink-0" />
                      <span className="text-[10px] font-medium text-slate-600 truncate">{item.title}</span>
                      <span className="text-[8px] text-slate-300 font-mono truncate ml-auto shrink-0">{item.slug}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Summary bar ── */

export function DiffSummaryBar({ mergedTree }: { mergedTree: MergedNode[] }) {
  const { addedCount, modifiedCount, deletedCount, movedCount, reorderCount } = useMemo(() => {
    let added = 0, modified = 0, deleted = 0, moved = 0, reorder = 0;
    function walk(nodes: MergedNode[]) {
      for (const n of nodes) {
        if (n.statuses.includes("added")) added++;
        if (n.statuses.includes("modified")) modified++;
        if (n.statuses.includes("deleted")) deleted++;
        if (n.statuses.includes("moved")) moved++;
        if (n.statuses.includes("reordered")) reorder++;
        walk(n.children);
      }
    }
    walk(mergedTree);
    return { addedCount: added, modifiedCount: modified, deletedCount: deleted, movedCount: moved, reorderCount: reorder };
  }, [mergedTree]);

  return (
    <div className="flex items-center gap-3 px-5 py-1.5 border-b border-slate-100 bg-slate-50/30 shrink-0 text-[10px]">
      <span className={addedCount > 0 ? "text-emerald-600 font-medium" : "text-slate-300 font-medium"}>+{addedCount} added</span>
      <span className={modifiedCount > 0 ? "text-blue-600 font-medium" : "text-slate-300 font-medium"}>~{modifiedCount} modified</span>
      <span className={deletedCount > 0 ? "text-red-500 font-medium" : "text-slate-300 font-medium"}>-{deletedCount} deleted</span>
      {movedCount > 0 && <span className="text-violet-600 font-medium">⇄{movedCount} moved</span>}
      {reorderCount > 0 && <span className="text-amber-600 font-medium">↕{reorderCount} reordered</span>}
    </div>
  );
}

export interface OrderChange {
  location: string;
  title: string;
  beforeOrder: string[];
  afterOrder: string[];
}

export interface MoveMapping {
  oldPath: string;
  newPath: string;
  children: { oldPath: string; newPath: string }[];
  deletedChildren: { slugPath: string; slug: string }[];
}

/* ── Hook: useTreeDiff — fetches tree-diff data and manages state ── */

export function useTreeDiff({ projectSlug, branchId, commitA, commitB }: {
  projectSlug: string;
  branchId: string;
  commitA: string | null;
  commitB: string | null;
}) {
  const [loading, setLoading] = useState(false);
  const [mergedTree, setMergedTree] = useState<MergedNode[]>([]);
  const [expandMap, setExpandMap] = useState<Record<string, boolean>>({});
  const [selectedDetail, setSelectedDetail] = useState<DetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("tree");
  const [orderChanges, setOrderChanges] = useState<OrderChange[]>([]);
  const [beforeNodes, setBeforeNodes] = useState<TreeNode[]>([]);
  const [afterNodes, setAfterNodes] = useState<TreeNode[]>([]);
  const [beforeRootOrder, setBeforeRootOrder] = useState<string[]>([]);
  const [afterRootOrder, setAfterRootOrder] = useState<string[]>([]);
  const [changePaths, setChangePaths] = useState<Set<string>>(new Set());
  const [moves, setMoves] = useState<MoveMapping[]>([]);
  const [rawChanges, setRawChanges] = useState<ChangeEntry[]>([]);

  const changelogRows = useMemo(() => computeGroupedRows(mergedTree), [mergedTree]);

  const toggleFolder = useCallback((sp: string) => {
    setExpandMap(prev => ({ ...prev, [sp]: !prev[sp] }));
  }, []);

  const handleSelectChange = useCallback(async (change: ChangeEntry) => {
    if (!projectSlug || !branchId || !commitA || !commitB) return;
    const slugA = change.originalSlugPath || change.slugPath;
    const slugB = change.slugPath;
    const params = new URLSearchParams();
    if (commitA) { params.set("commit_a", commitA); params.set("slug_a", slugA); }
    if (commitB) { params.set("commit_b", commitB); params.set("slug_b", slugB); }
    const title = change.slugPath.split("/").pop() || "";
    setDetailLoading(true);
    setSelectedDetail({ slugPath: change.slugPath, title, change, beforeBody: "", afterBody: "" });
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/node-diff?${params}`);
      if (res.ok) {
        const d = await res.json();
        setSelectedDetail({
          slugPath: change.slugPath,
          title: d.afterTitle || d.beforeTitle || title,
          change,
          beforeBody: d.beforeBody || "",
          afterBody: d.afterBody || "",
          nodeType: d.nodeType,
          beforeTitle: d.beforeTitle,
          afterTitle: d.afterTitle,
          changedAssets: d.changedAssets,
          beforeSpec: d.beforeSpec || null,
          afterSpec: d.afterSpec || null,
        });
      }
    } catch { /* keep empty */ }
    setDetailLoading(false);
  }, [commitA, commitB, projectSlug, branchId]);

  // Fetch tree-diff when commits change
  const fetchTreeDiff = useCallback(async () => {
    if (!projectSlug || !branchId || !commitA || !commitB) return;
    setLoading(true);
    setSelectedDetail(null);
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/tree-diff/${commitA}/${commitB}`);
      if (!res.ok) { console.error("[tree-diff] API returned", res.status, res.statusText); setLoading(false); return; }
      const data = await res.json();
      console.log("[tree-diff] API response:", { changes: data.changes?.length, beforeNodes: data.beforeNodes?.length, afterNodes: data.afterNodes?.length });
      const beforeRootOrder: string[] = data.beforeRootOrder || [];
      const afterRootOrder: string[] = data.afterRootOrder || [];
      const changes: ChangeEntry[] = data.changes || [];
      const beforeNodes: TreeNode[] = data.beforeNodes || [];
      const afterNodes: TreeNode[] = data.afterNodes || [];
      const movesData: MoveMapping[] = data.moves || [];

      const tree = buildDiffMergedTree(beforeNodes, afterNodes, changes, beforeRootOrder, afterRootOrder);
      setMergedTree(tree);
      setBeforeNodes(beforeNodes);
      setAfterNodes(afterNodes);
      setBeforeRootOrder(beforeRootOrder);
      setAfterRootOrder(afterRootOrder);
      setChangePaths(new Set(changes.flatMap(c => c.originalSlugPath ? [c.slugPath, c.originalSlugPath] : [c.slugPath])));
      setMoves(movesData);
      setRawChanges(changes);

      // Auto-expand folders with changes
      const map: Record<string, boolean> = {};
      function walk(nodes: MergedNode[]) {
        for (const n of nodes) {
          if (n.children.length > 0) map[n.slugPath] = subtreeHasChanges(n);
          walk(n.children);
        }
      }
      walk(tree);
      setExpandMap(map);

      // Compute order changes
      const ocs: OrderChange[] = [];
      // Root order
      if (JSON.stringify(beforeRootOrder) !== JSON.stringify(afterRootOrder)) {
        ocs.push({ location: "root", title: "Root order", beforeOrder: beforeRootOrder, afterOrder: afterRootOrder });
      }
      // Per-node children_order changes
      const beforeByPath = new Map(beforeNodes.map(n => [n.slugPath, n]));
      const afterByPath = new Map(afterNodes.map(n => [n.slugPath, n]));
      for (const c of changes) {
        if (c.changeTypes?.includes("reorder")) {
          const bn = beforeByPath.get(c.slugPath);
          const an = afterByPath.get(c.slugPath);
          if (bn && an) {
            ocs.push({
              location: c.slugPath,
              title: an.title || bn.title || c.slugPath,
              beforeOrder: bn.order || [],
              afterOrder: an.order || [],
            });
          }
        }
      }
      setOrderChanges(ocs);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectSlug, branchId, commitA, commitB]);

  const clearDetail = useCallback(() => setSelectedDetail(null), []);

  return {
    loading, mergedTree, expandMap, selectedDetail, detailLoading,
    tab, setTab, changelogRows, toggleFolder, handleSelectChange,
    setSelectedDetail, clearDetail, fetchTreeDiff, orderChanges,
    beforeNodes, afterNodes, beforeRootOrder, afterRootOrder, changePaths, moves, rawChanges,
  };
}

/* ── StructuralComparePanel — two-column before/after tree showing order ──
   Each side renders its OWN order so reorder differences are visible.
   Clicking a node selects it on both sides and scrolls to match. */

function StructuralTreeSide({ nodes, rootOrder, changePaths, movedPaths, expanded, onToggle, selectedSlugPath, onSelectNode, changeMap, onSelectChange }: {
  nodes: TreeNode[];
  rootOrder: string[];
  changePaths: Set<string>;
  movedPaths: Set<string>;
  expanded: Set<string>;
  onToggle: (sp: string) => void;
  selectedSlugPath: string | null;
  onSelectNode: (sp: string) => void;
  changeMap?: Map<string, ChangeEntry>;
  onSelectChange?: (c: ChangeEntry) => void;
}) {
  const { sortChildren, roots } = useMemo(() => {
    const allPaths = new Set(nodes.map(n => n.slugPath));
    // Group children by parent — root nodes are those whose parentPath is falsy
    // or whose parentPath doesn't exist in the node set (same logic as buildChildIndex)
    const cMap = new Map<string, TreeNode[]>();
    const ROOT = "__root__";
    for (const n of nodes) {
      const key = n.parentPath && allPaths.has(n.parentPath) ? n.parentPath : ROOT;
      if (!cMap.has(key)) cMap.set(key, []);
      cMap.get(key)!.push(n);
    }
    function sc(parentKey: string, order: string[]) {
      const kids = cMap.get(parentKey) || [];
      const om = new Map(order.map((s, i) => [s, i]));
      return [...kids].sort((a, b) => (om.get(a.slug) ?? 999) - (om.get(b.slug) ?? 999));
    }
    const rootNodes = sc(ROOT, rootOrder);
    return { sortChildren: sc, roots: rootNodes };
  }, [nodes, rootOrder]);

  function renderNode(node: TreeNode, depth: number): React.ReactNode {
    const sp = node.slugPath;
    const isChanged = changePaths.has(sp);
    const kids = sortChildren(sp, node.order || []);
    const hasKids = kids.length > 0;
    const isExpanded = expanded.has(sp);
    const isSelected = selectedSlugPath === sp;
    return (
      <div key={node.id} data-slugpath={sp}>
        <div
          style={{ paddingLeft: depth * 14 }}
          className={`flex items-center gap-1 py-[3px] px-2 text-[10px] cursor-pointer transition-colors ${
            isSelected ? "bg-indigo-50 text-indigo-800 font-medium ring-1 ring-inset ring-indigo-200"
              : isChanged ? "bg-amber-50/80 text-amber-800 font-medium hover:bg-amber-100/60"
              : "text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => {
            onSelectNode(sp);
          }}
        >
          {hasKids ? (
            <button onClick={(e) => { e.stopPropagation(); onToggle(sp); }} className="shrink-0 p-0 hover:text-slate-900">
              {isExpanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            </button>
          ) : <span className="size-3 shrink-0" />}
          {hasKids
            ? <FolderOpen className={`size-3 shrink-0 ${isChanged ? "text-amber-500" : "text-amber-400"}`} />
            : <FileText className={`size-3 shrink-0 ${isChanged ? "text-amber-400" : "text-slate-400"}`} />}
          <span className="truncate">{node.title}</span>
          {(() => {
            const showEye = isChanged && !hasKids && !movedPaths.has(sp) && changeMap && onSelectChange;
            const change = showEye ? changeMap.get(sp) : null;
            return (
              <span className="flex items-center gap-0.5 ml-auto shrink-0">
                {change && (
                  <button onClick={e => { e.stopPropagation(); onSelectChange!(change); }}
                    className="p-0.5 rounded text-blue-400 hover:bg-blue-50 transition-colors" title="View diff">
                    <Eye className="size-2.5" />
                  </button>
                )}
                {isChanged && movedPaths.has(sp) && <span className="text-[8px] px-1 py-px rounded font-semibold bg-violet-100 text-violet-600">MOVE</span>}
                {isChanged && !movedPaths.has(sp) && <span className="text-[8px] px-1 py-px rounded font-semibold bg-blue-100 text-blue-600">MOD</span>}
              </span>
            );
          })()}
        </div>
        {hasKids && isExpanded && kids.map(k => renderNode(k, depth + 1))}
      </div>
    );
  }
  return <>{roots.map(r => renderNode(r, 0))}</>;
}

export function StructuralComparePanel({ beforeNodes, afterNodes, beforeRootOrder, afterRootOrder, changePaths, movedPaths, changeMap, onSelectChange }: {
  beforeNodes: TreeNode[];
  afterNodes: TreeNode[];
  beforeRootOrder: string[];
  afterRootOrder: string[];
  changePaths: Set<string>;
  movedPaths: Set<string>;
  changeMap?: Map<string, ChangeEntry>;
  onSelectChange?: (c: ChangeEntry) => void;
}) {
  const autoExpanded = useMemo(() => {
    const result = new Set<string>();
    function walkSide(nodes: TreeNode[]) {
      const byPath = new Map<string, TreeNode>();
      for (const n of nodes) byPath.set(n.slugPath, n);
      for (const sp of changePaths) {
        let cur = sp;
        while (cur) {
          if (byPath.has(cur)) result.add(cur);
          const idx = cur.lastIndexOf("/");
          if (idx < 0) break;
          cur = cur.substring(0, idx);
        }
      }
    }
    walkSide(beforeNodes);
    walkSide(afterNodes);
    return result;
  }, [beforeNodes, afterNodes, changePaths]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(autoExpanded));
  const [selectedSlugPath, setSelectedSlugPath] = useState<string | null>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpanded(prev => { const next = new Set(prev); for (const sp of autoExpanded) next.add(sp); return next; });
  }, [autoExpanded]);

  const toggle = useCallback((sp: string) => {
    setExpanded(prev => { const next = new Set(prev); if (next.has(sp)) next.delete(sp); else next.add(sp); return next; });
  }, []);

  const handleSelect = useCallback((sp: string) => {
    setSelectedSlugPath(prev => prev === sp ? null : sp);
    for (const ref of [leftRef, rightRef]) {
      if (!ref.current) continue;
      const el = ref.current.querySelector(`[data-slugpath="${CSS.escape(sp)}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="flex shrink-0 border-b border-slate-200">
        <div className="flex-1 px-3 py-1.5 bg-red-50/50 flex items-center gap-1.5 border-r border-slate-200">
          <div className="size-2 rounded-full bg-red-400" />
          <span className="text-[10px] font-semibold text-red-700">Before</span>
        </div>
        <div className="flex-1 px-3 py-1.5 bg-emerald-50/50 flex items-center gap-1.5">
          <div className="size-2 rounded-full bg-emerald-400" />
          <span className="text-[10px] font-semibold text-emerald-700">After</span>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex min-h-0">
        <div ref={leftRef} className="flex-1 border-r border-slate-200 px-1 py-1 overflow-auto scrollbar-mac">
          <StructuralTreeSide nodes={beforeNodes} rootOrder={beforeRootOrder} changePaths={changePaths} movedPaths={movedPaths}
            expanded={expanded} onToggle={toggle} selectedSlugPath={selectedSlugPath} onSelectNode={handleSelect}
            changeMap={changeMap} onSelectChange={onSelectChange} />
        </div>
        <div ref={rightRef} className="flex-1 px-1 py-1 overflow-auto scrollbar-mac">
          <StructuralTreeSide nodes={afterNodes} rootOrder={afterRootOrder} changePaths={changePaths} movedPaths={movedPaths}
            expanded={expanded} onToggle={toggle} selectedSlugPath={selectedSlugPath} onSelectNode={handleSelect}
            changeMap={changeMap} onSelectChange={onSelectChange} />
        </div>
      </div>
    </div>
  );
}

/* ── UnifiedDiffView — single structural comparison with clickable content detail ── */

export function UnifiedDiffView({
  beforeNodes, afterNodes, beforeRootOrder, afterRootOrder, changePaths,
  mergedTree, orderChanges, onSelectChange,
  selectedDetail, detailLoading, onClearDetail,
}: {
  beforeNodes: TreeNode[];
  afterNodes: TreeNode[];
  beforeRootOrder: string[];
  afterRootOrder: string[];
  changePaths: Set<string>;
  mergedTree: MergedNode[];
  orderChanges: OrderChange[];
  onSelectChange: (c: ChangeEntry) => void;
  selectedDetail: DetailData | null;
  detailLoading: boolean;
  onClearDetail: () => void;
}) {
  // Build movedPaths from changes that have "moved" in actions
  const movedPaths = useMemo(() => {
    const result = new Set<string>();
    function walk(nodes: MergedNode[]) {
      for (const n of nodes) {
        if (n.statuses.includes("moved")) result.add(n.slugPath);
        walk(n.children);
      }
    }
    walk(mergedTree);
    return result;
  }, [mergedTree]);

  // Build changeMap for content detail on click
  const changeMap = useMemo(() => {
    const m = new Map<string, ChangeEntry>();
    function walk(nodes: MergedNode[]) {
      for (const n of nodes) {
        if (n.change) m.set(n.slugPath, n.change);
        walk(n.children);
      }
    }
    walk(mergedTree);
    return m;
  }, [mergedTree]);

  if (mergedTree.length === 0) {
    return <div className="py-8 text-center text-[12px] text-slate-400">No changes.</div>;
  }

  // Detail view takes over
  if (selectedDetail) {
    return <DetailView detail={selectedDetail} detailLoading={detailLoading} onBack={onClearDetail} />;
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <StructuralComparePanel
        beforeNodes={beforeNodes} afterNodes={afterNodes}
        beforeRootOrder={beforeRootOrder} afterRootOrder={afterRootOrder}
        changePaths={changePaths} movedPaths={movedPaths}
        changeMap={changeMap} onSelectChange={onSelectChange}
      />
      {orderChanges.length > 0 && <OrderChangesView orderChanges={orderChanges} />}
    </div>
  );
}

/* ── Order changes view ── */

export function OrderChangesView({ orderChanges }: { orderChanges: OrderChange[] }) {
  if (orderChanges.length === 0) return null;
  return (
    <div className="px-5 py-3 space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-700">
        <span className="text-[10px]">↕</span> Order changes
      </div>
      {orderChanges.map((oc, i) => (
        <div key={i} className="border border-violet-200 rounded-lg px-3 py-2 bg-violet-50/30">
          <div className="text-[11px] font-medium text-slate-700 mb-1">
            {oc.location === "root" ? "Root section order" : oc.title}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <div className="text-[9px] text-slate-400 mb-0.5">Before</div>
              <div className="flex flex-wrap gap-1">
                {oc.beforeOrder.map((s, j) => (
                  <span key={j} className={`px-1.5 py-0.5 rounded font-mono ${
                    !oc.afterOrder.includes(s) ? "bg-red-100 text-red-600 line-through" : "bg-slate-100 text-slate-600"
                  }`}>{s}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[9px] text-slate-400 mb-0.5">After</div>
              <div className="flex flex-wrap gap-1">
                {oc.afterOrder.map((s, j) => (
                  <span key={j} className={`px-1.5 py-0.5 rounded font-mono ${
                    !oc.beforeOrder.includes(s) ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-600"
                  }`}>{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Section drill-down view ──
 * Default: shows top-level sections as a list with status badges + order.
 * Click a section → scoped tree diff of that section's children.
 * Click a leaf page → content detail view.
 */

export interface SectionSummary {
  node: MergedNode;
  changeCount: number;
  hasReorder: boolean;
}

function countChanges(node: MergedNode): number {
  let c = node.statuses.length > 0 ? 1 : 0;
  for (const ch of node.children) c += countChanges(ch);
  return c;
}

function nodeHasReorder(node: MergedNode): boolean {
  if (node.change?.changeTypes?.includes("reorder")) return true;
  return node.children.some(nodeHasReorder);
}

export function buildSectionSummaries(mergedTree: MergedNode[]): SectionSummary[] {
  return mergedTree.map(node => ({
    node,
    changeCount: countChanges(node),
    hasReorder: nodeHasReorder(node),
  }));
}

const statusBadge: Record<string, { label: string; bg: string; text: string }> = {
  added:     { label: "ADD",  bg: "bg-emerald-50", text: "text-emerald-600" },
  deleted:   { label: "DEL",  bg: "bg-red-50",     text: "text-red-500" },
  modified:  { label: "MOD",  bg: "bg-blue-50",    text: "text-blue-600" },
  moved:     { label: "MOVE", bg: "bg-violet-50",  text: "text-violet-600" },
  renamed:   { label: "REN",  bg: "bg-fuchsia-50", text: "text-fuchsia-600" },
  reordered: { label: "ORD",  bg: "bg-amber-50",   text: "text-amber-600" },
};

export function SectionDrillView({
  mergedTree, orderChanges, expandMap, onToggle, onSelectChange, selectedDetail, detailLoading, onClearDetail,
}: {
  mergedTree: MergedNode[];
  orderChanges: OrderChange[];
  expandMap: Record<string, boolean>;
  onToggle: (sp: string) => void;
  onSelectChange: (c: ChangeEntry) => void;
  selectedDetail: DetailData | null;
  detailLoading: boolean;
  onClearDetail: () => void;
}) {
  const [drilledSection, setDrilledSection] = useState<MergedNode | null>(null);

  const sections = useMemo(() => buildSectionSummaries(mergedTree), [mergedTree]);
  const rootOrderChange = orderChanges.find(oc => oc.location === "root");

  // Reset drill when tree changes
  const treeKey = mergedTree.map(n => n.slugPath).join(",");
  const [prevKey, setPrevKey] = useState(treeKey);
  if (treeKey !== prevKey) { setPrevKey(treeKey); setDrilledSection(null); }

  if (mergedTree.length === 0) {
    return <div className="py-8 text-center text-[12px] text-slate-400">No changes.</div>;
  }

  // Content detail view (deepest level)
  if (selectedDetail) {
    return <DetailView detail={selectedDetail} detailLoading={detailLoading} onBack={onClearDetail} />;
  }

  // Scoped tree diff for a single section
  if (drilledSection) {
    const sectionOrderChanges = orderChanges.filter(
      oc => oc.location === drilledSection.slugPath || oc.location.startsWith(drilledSection.slugPath + "/")
    );
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-100 bg-slate-50/30 shrink-0">
          <button onClick={() => setDrilledSection(null)}
            className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600 transition-colors">
            <ArrowLeft className="size-3" /> Sections
          </button>
          <span className="text-[11px] text-slate-300">|</span>
          <FolderOpen className="size-3 text-amber-500" />
          <span className="text-[11px] font-medium text-slate-700">{drilledSection.draftTitle || drilledSection.sourceTitle || drilledSection.title}</span>
          {drilledSection.statuses.length > 0 && (
            <span className="flex items-center gap-0.5">
              {drilledSection.statuses.map(s => {
                const b = statusBadge[s]; if (!b) return null;
                return <span key={s} className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${b.bg} ${b.text}`}>{b.label}</span>;
              })}
            </span>
          )}
        </div>
        <DiffSummaryBar mergedTree={[drilledSection]} />
        <TreeDiffView
          mergedTree={drilledSection.children}
          expandMap={expandMap}
          onToggle={onToggle}
          onSelect={onSelectChange}
        />
        {sectionOrderChanges.length > 0 && <OrderChangesView orderChanges={sectionOrderChanges} />}
      </div>
    );
  }

  // Default: section list with order
  return (
    <div className="flex-1 overflow-y-auto scrollbar-mac">
      {/* Root order change */}
      {rootOrderChange && (
        <div className="px-5 pt-4 pb-1">
          <div className="border border-violet-200 rounded-lg px-3 py-2 bg-violet-50/30">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 mb-1.5">
              <span className="text-[10px]">↕</span> Section order changed
            </div>
            <div className="grid grid-cols-2 gap-3 text-[10px]">
              <div>
                <div className="text-[9px] text-slate-400 mb-1">Before</div>
                <div className="space-y-0.5">
                  {rootOrderChange.beforeOrder.map((s, i) => {
                    const removed = !rootOrderChange.afterOrder.includes(s);
                    const moved = !removed && rootOrderChange.afterOrder.indexOf(s) !== i;
                    return (
                      <div key={i} className={`flex items-center gap-1.5 px-2 py-1 rounded ${removed ? "bg-red-50 line-through text-red-500" : moved ? "bg-amber-50/50 text-slate-600" : "text-slate-600"}`}>
                        <span className="text-[9px] text-slate-300 w-3 shrink-0">{i + 1}</span>
                        <span className="font-mono truncate">{s}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-slate-400 mb-1">After</div>
                <div className="space-y-0.5">
                  {rootOrderChange.afterOrder.map((s, i) => {
                    const added = !rootOrderChange.beforeOrder.includes(s);
                    const moved = !added && rootOrderChange.beforeOrder.indexOf(s) !== i;
                    return (
                      <div key={i} className={`flex items-center gap-1.5 px-2 py-1 rounded ${added ? "bg-emerald-50 text-emerald-600" : moved ? "bg-amber-50/50 text-slate-600" : "text-slate-600"}`}>
                        <span className="text-[9px] text-slate-300 w-3 shrink-0">{i + 1}</span>
                        <span className="font-mono truncate">{s}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section list */}
      <div className="px-5 py-3 space-y-1">
        {sections.map(({ node, changeCount, hasReorder }) => {
          const hasChanges = changeCount > 0 || hasReorder;
          const isLeaf = node.children.length === 0;
          return (
            <div key={node.slugPath}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors ${
                hasChanges ? "border-slate-200 hover:bg-slate-50/80 cursor-pointer" : "border-slate-100 opacity-60"
              }`}
              onClick={() => {
                if (hasChanges) setDrilledSection(node);
              }}
            >
              {isLeaf
                ? <FileText className="size-4 text-slate-400 shrink-0" />
                : <FolderOpen className="size-4 text-amber-500 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-slate-700 truncate">
                  {node.draftTitle || node.sourceTitle || node.title}
                </div>
                <div className="text-[9px] text-slate-400 font-mono truncate">{node.slugPath}</div>
              </div>
              {isLeaf && node.change && (
                <button onClick={e => { e.stopPropagation(); onSelectChange(node.change!); }}
                  className="p-1 rounded text-blue-400 hover:bg-blue-50 transition-colors shrink-0" title="View diff">
                  <Eye className="size-3.5" />
                </button>
              )}
              <div className="flex items-center gap-1.5 shrink-0">
                {hasReorder && <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-semibold">↕</span>}
                {node.statuses.map(s => {
                  const badge = statusBadge[s]; if (!badge || !badge.label) return null;
                  return <span key={s} className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${badge.bg} ${badge.text}`}>{badge.label}</span>;
                })}
                {changeCount > 0 && node.children.length > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">{changeCount}</span>
                )}
                {hasChanges && !isLeaf && <ChevronRight className="size-3.5 text-slate-400" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
